import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, Users, Flame, Sparkles, Zap, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import ImageCarousel from './ImageCarousel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FeaturedTrips = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  const burnIcons = {
    1: Sparkles,
    2: Flame,
    3: Zap
  };

  const burnColors = {
    1: 'from-purple-500 to-pink-500',
    2: 'from-orange-500 to-amber-500',
    3: 'from-red-500 to-orange-600'
  };

  const burnBgColors = {
    1: 'bg-purple-600 text-white',
    2: 'bg-orange-500 text-white',
    3: 'bg-red-600 text-white'
  };

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const response = await fetch(`${API_URL}/api/trips?featured=true&lang=${language}`);
        const data = await response.json();
        setTrips(data.trips || []);
      } catch (error) {
        console.error('Error fetching trips:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
  }, [language]);

  if (loading) {
    return (
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-gray-200 rounded w-1/3 mx-auto"></div>
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-96 bg-gray-200 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-4 bg-gray-50" data-testid="featured-trips-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-[#84bc00]/10 text-[#84bc00] text-sm font-bold rounded-full mb-4">
            {language === 'es' ? 'VIAJES DESTACADOS' : 'FEATURED TRIPS'}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
            {language === 'es' ? 'Experiencias que te cambian' : 'Experiences that change you'}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {language === 'es' 
              ? 'Nuestros viajes más populares, curados para todos los niveles de intensidad' 
              : 'Our most popular trips, curated for every intensity level'}
          </p>
        </div>

        {/* Trips Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {trips.slice(0, 6).map((trip, index) => {
            const BurnIcon = burnIcons[trip.burn_level] || Flame;
            const burnGradient = burnColors[trip.burn_level] || burnColors[2];
            const burnBg = burnBgColors[trip.burn_level] || burnBgColors[2];

            return (
              <div
                key={trip.id}
                data-testid={`trip-card-${trip.id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                {/* Image Carousel */}
                <div className="relative h-56 overflow-hidden">
                  <ImageCarousel
                    mainImage={trip.image}
                    images={trip.images || []}
                    alt={trip.name}
                    className="h-full"
                    aspectRatio="video"
                    showDots={true}
                    showArrows={true}
                  />
                  {/* Burn Level Badge */}
                  <div className={`absolute top-4 left-4 px-3 py-1.5 rounded-full ${burnBg} backdrop-blur-sm flex items-center gap-1.5 z-20`}>
                    <BurnIcon className="w-4 h-4" />
                    <span className="text-xs font-bold">{trip.burn_label}</span>
                  </div>
                  {/* Price */}
                  <div className="absolute bottom-4 right-4 bg-black/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg z-20">
                    <span className="text-xs text-gray-300">{language === 'es' ? 'desde' : 'from'}</span>
                    <span className="text-lg font-black ml-1">${trip.price?.toLocaleString()}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Location & Duration */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{trip.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{trip.duration}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#84bc00] transition-colors">
                    {trip.name}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {trip.description}
                  </p>

                  {/* Vibes Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {trip.vibes?.map((vibe, i) => (
                      <span 
                        key={i}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full capitalize"
                      >
                        {vibe}
                      </span>
                    ))}
                  </div>

                  {/* CTA */}
                  <Button
                    onClick={() => navigate(`/trips/${trip.id}`)}
                    className={`w-full bg-gradient-to-r ${burnGradient} hover:opacity-90 text-white font-bold py-3 rounded-lg transition-all`}
                  >
                    {language === 'es' ? 'Ver Detalles' : 'View Details'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* View All CTA */}
        <div className="text-center mt-12">
          <Button
            onClick={() => navigate('/trips')}
            variant="outline"
            className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-bold px-8 py-4 rounded-lg transition-all"
            data-testid="view-all-trips-btn-featured"
          >
            {language === 'es' ? 'VER TODOS LOS VIAJES' : 'VIEW ALL TRIPS'}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedTrips;
